/**
 * Faction Demand Economy — L3 Operations pricing (M3 S5).
 *
 * This is what makes the DESTINATION matter. Without it every region pays the
 * same and the map is scenery: a convoy's route was already priced by terrain,
 * but where it ended up was a free variable.
 *
 * The one idea: a region pays a premium for the material signatures its own
 * faction mix does not produce. Verdant Reclamation is 92% wilders, so wilders
 * goods are the thing it already has — dominion and veil cargo is what it wants.
 *
 * AND A MARKET THAT IS FED STOPS BEING HUNGRY. The mix above is static, so on
 * its own it is a table the player solves once: pick the signature, read off the
 * region that pays ×2.00, and run that pairing forever. Every route costs the
 * same time, cargo and energy, so a fixed premium made destination a lookup
 * rather than a decision. Saturation is what turns it back into one — delivering
 * into a region consumes its appetite for that faction's goods, and the appetite
 * refills on a clock. The best destination is still the best destination; it
 * just stops being the best destination once you have used it.
 *
 * DERIVED, NOT AUTHORED. There is no hand-written economy table here, because a
 * hand-written one drifts: add a room to a region, and an authored demand table
 * silently keeps pricing the old geography. The mix is computed from the same
 * thing that decides what actually DROPS in a region — the faction of each
 * gate/hazard room's primary skill — so demand and supply cannot disagree.
 * This reproduces §1 of tools/loot-equilibrium.mjs exactly; that tool is the
 * offline analysis of the same numbers.
 *
 * No browser dependencies — the simulator runs this too.
 *
 * See docs/layers/3-syndicate/design.md §6.
 */

import * as config from '../constants.js';
import { gameState } from './gameState-core.js';
import { getSkillFaction } from './util.js';

/**
 * Room draw rates per raid, mirroring the income model in loot-equilibrium.mjs.
 *
 * Hazards outnumber gates 2:1 in a typical path, so a region's character is set
 * more by its hazards than its gates. Weighting them equally would misread every
 * region whose gates and hazards pull toward different factions.
 */
const GATES_PER_RAID = 2;
const HAZARDS_PER_RAID = 4;

/**
 * Multiplier band. A region's own dominant signature pays flat; the signature it
 * lacks entirely pays double.
 *
 * Provisional — S7 calibrates. The band is the lever: widening it makes
 * destination choice dominate everything else about an operation, narrowing it
 * makes the map scenery again.
 */
export const DEMAND_MIN = 1.0;
export const DEMAND_MAX = 2.0;

/**
 * Writ trickle threshold. A delivery at or above this multiplier earns ONE extra
 * Writ — but only on templates that already pay Writs, so the trickle rides the
 * long routes rather than turning Short Hauls into a Writ farm.
 *
 * Deliberately a flat +1 rather than a scaling bonus: founding a Syndicate pays
 * floor(k·√eraMarks), tens of Writs at a time, and that has to stay the dominant
 * source. This is a trickle by construction, not by tuning.
 */
export const DEMAND_WRIT_THRESHOLD = 1.5;

// Region mixes are a pure function of static config; compute once.
let mixCache = null;

/**
 * What share of a region's loot-bearing rooms belongs to each faction.
 *
 * @returns {Object<string, Object<string, number>>} regionId -> faction -> share
 */
function allRegionMixes() {
    if (mixCache) return mixCache;
    mixCache = {};
    for (const [regionId, region] of Object.entries(config.raidRegions || {})) {
        const share = {};
        for (const faction of Object.keys(config.factionSkills || {})) share[faction] = 0;

        let total = 0;
        for (const [rooms, perRaid] of [
            [region.gates || [], GATES_PER_RAID],
            [region.hazards || [], HAZARDS_PER_RAID],
        ]) {
            if (!rooms.length) continue;
            // Rooms are drawn uniformly from the region's pool, so a region with
            // few gates weights each of them more heavily.
            const weight = perRaid / rooms.length;
            for (const room of rooms) {
                const faction = getSkillFaction(room.primary);
                if (faction && faction in share) share[faction] += weight;
                total += weight;
            }
        }
        if (total > 0) {
            for (const faction of Object.keys(share)) share[faction] /= total;
        }
        mixCache[regionId] = share;
    }
    return mixCache;
}

/** A region's faction mix as shares summing to 1 (or all zero if unknown). */
export function regionFactionMix(regionId) {
    const mix = allRegionMixes()[regionId];
    if (mix) return { ...mix };
    return Object.fromEntries(Object.keys(config.factionSkills || {}).map(f => [f, 0]));
}

/**
 * The premium a region pays for a faction's signature, BEFORE saturation.
 *
 * The list price: a pure function of static config, and the number a player
 * should think of as the region's character. What a convoy actually collects is
 * `factionDemandMultiplier`, which is this discounted by how recently the market
 * was fed. Kept separate because the planner shows both — a price that has
 * fallen is only legible next to the price it fell from.
 *
 * Scaled against the region's OWN dominant share, not against 100%. Absolute
 * coverage would have priced a mixed region's home signature at a premium
 * — Cradle Expanse is only 50% dominion, so dominion cargo delivered to the
 * dominion homeland would have paid ×1.5, which is nonsense. Relative to its own
 * peak, a region's dominant signature always pays flat and only genuine gaps pay
 * a premium. Mixed regions therefore pay small premiums across the board and
 * specialists pay large ones for exactly what they lack — which is the right
 * shape: a region that produces a bit of everything wants nothing badly.
 */
export function baseFactionDemand(regionId, faction) {
    if (!faction) return DEMAND_MIN;
    const mix = regionFactionMix(regionId);
    const peak = Math.max(0, ...Object.values(mix));
    if (peak <= 0) return DEMAND_MIN;
    const relative = Math.min(1, (mix[faction] || 0) / peak);
    return +(DEMAND_MAX - relative * (DEMAND_MAX - DEMAND_MIN)).toFixed(2);
}

// ---------------------------------------------------------------------------
// Market saturation
// ---------------------------------------------------------------------------

/**
 * CREDITS OF GOODS that fully flatten one region's appetite for one faction's.
 *
 * Measured in the manifest a convoy actually delivered, not in convoys: a Grand
 * Circuit lands seven times a Short Haul's cargo, and a flat per-op step would
 * price those the same. Volume-driven falls out correctly for free, and nothing
 * here needs to know a template exists.
 *
 * ⚠ DENOMINATED IN VALUE, NOT UNITS, since manifests became a credit budget
 * (`planCargo`). A rare is 23 commons on the derived ladder, so a unit count
 * would let a convoy of three rares satiate a market as little as three offcuts.
 * The conversion is exact — 50 units of common was 250 credits — so this is the
 * same appetite restated, and the calibration behind it still holds.
 *
 * CALIBRATED, and the opening guess was an order of magnitude out. 160 units on
 * a 24-hour recovery was reasoned from three slots running Grand Circuits back
 * to back; the simulator actually dispatches ~0.5 convoys a day, mostly Short
 * Hauls, because the dock is a per-era budget and cargo — not slots — is what
 * binds. Against that cadence a market refilled ~10x faster than it was fed and
 * the mechanic did nothing: 91% of convoys still went to one destination.
 *
 * Measured, one 89-day run each (`npm run sim`, `ops.topRegionSharePct`):
 *
 *     appetite  recovery   top destination   mean demand   mean saturation
 *        160       24h          91.3%           x1.96           0.16
 *         60       72h          61.7%           x1.87           0.50
 *         50       72h          52-57%          x1.79-1.84      0.60
 *         40       72h          48.0%           x1.81           0.59
 *
 * 50 over 40 because 40 puts BOTH larger templates over the cap — a Long Haul
 * and a Grand Circuit would flatten a market equally and the volume ladder
 * collapses at the top. At 50 a Short Haul takes 0.4 of a market, a Long Haul
 * 0.9, and only a Grand Circuit saturates one outright. The rotation difference
 * between the two (48% against 52-57%) is inside the run-to-run spread; the
 * template ladder is not.
 *
 * ⚠ THE CONVERSION IS NOT A STRAIGHT RESTATEMENT, and assuming it was is a trap
 * worth naming. The obvious move is 50 commons x 5 = 250, but the old unit count
 * badly under-weighted rares: a Long Haul's 45 commons + 1 rare counted as 46
 * units (0.92 of a market) and is 340 credits (1.36 — capped, so it would
 * flatten a market outright). That collapses the top of the ladder in exactly
 * the way 40 was rejected for.
 *
 * 400 keeps the ladder's SHAPE, which is what the calibration actually bought:
 *
 *     short-haul     100 / 400 = 0.25
 *     long-haul      340 / 400 = 0.85
 *     grand-circuit  745 / 400 = 1.00  (caps — only this class flattens a market)
 *
 * Short Hauls now bite a little less than the 0.4 they did on unit count. That
 * is the correction, not a regression: three offcuts and three encrypted chips
 * were never the same delivery, and the unit count said they were.
 */
export const REGION_APPETITE = 3200;

/**
 * REAL hours for a fully saturated market to recover its list price. Linear.
 *
 * Real hours, NOT world-clock days — this is the one number that would be
 * silently wrong if it followed the clock the map draws. Operations run on real
 * time (`HOUR_MS`; a Grand Circuit is twelve real hours) while a world-clock day
 * is ten real minutes, so keying recovery to `DAY_MS` would refill every market
 * seventy-two times over between one convoy and the next and the whole mechanic
 * would do nothing at all.
 *
 * Linear rather than exponential because the planner has to be able to say
 * "recovers in ~13h" and have that be true. An exponential tail is more natural
 * and completely unreadable.
 *
 * 72h, not the 24h first guessed, for the cadence reason set out on
 * REGION_APPETITE: convoys arrive far too slowly for a one-day market to ever
 * be caught mid-recovery. Three days also puts recovery on a longer clock than
 * any single operation (12h at most), which is what stops the answer being
 * "wait for it" — a market you fed is still flat when the convoy you sent
 * elsewhere gets home.
 */
export const SATIATION_RECOVERY_HOURS = 72;

const RECOVERY_MS = SATIATION_RECOVERY_HOURS * 3600 * 1000;

const clamp01 = (n) => Math.min(1, Math.max(0, n));

const satiationKey = (regionId, faction) => `${regionId}:${faction}`;

/**
 * The saturation ledger, keyed by (region, faction).
 *
 * That granularity is the multiplier's own: the price is per-faction, so a
 * finer per-SIGNATURE ledger would be fake precision the player could game by
 * rotating the five signatures of one faction through the same fat region,
 * while a coarser per-REGION one would suppress a region's dominion price
 * because you sold it veil goods. Fifteen cells, one per price the game quotes.
 *
 * Lives on `syndicate` because it is world state that outlives an era — see the
 * founding note on `satiationLevel`. Returns null before the branch exists,
 * which is simply "nothing has ever been delivered".
 */
function satiationStore() {
    const syndicate = gameState.syndicate;
    if (!syndicate) return null;
    if (!syndicate.demand) syndicate.demand = {};
    return syndicate.demand;
}

/**
 * How fed a market is, 0 (hungry) to 1 (flat), decayed to `now`.
 *
 * DECAY IS COMPUTED ON READ, never ticked. A stored `{level, at}` pair plus the
 * elapsed time is the whole mechanic: no timer to register, no catch-up pass on
 * load, offline recovery for free, and the same answer however many times it is
 * read. A ticker would have had to be reconciled against every one of those.
 *
 * NOT cleared at founding. Saturation is a fact about regional markets, like the
 * world clock — wiping it would hand out a fresh ×2.00 window every prestige and
 * quietly turn founding-timing into a market exploit. `foundSyndicateCore` is
 * deliberately not touched; §2's wipe/persist ledger lists this in the persist
 * column.
 */
export function satiationLevel(regionId, faction, now = Date.now()) {
    const cell = satiationStore()?.[satiationKey(regionId, faction)];
    if (!cell) return 0;
    const elapsed = Math.max(0, now - (cell.at || 0));
    return clamp01((cell.level || 0) - elapsed / RECOVERY_MS);
}

/** Milliseconds until this market is back at its list price. */
export function satiationRecoveryMs(regionId, faction, now = Date.now()) {
    return Math.round(satiationLevel(regionId, faction, now) * RECOVERY_MS);
}

/**
 * Feed a market: record `units` of a signature's cargo landing in a region.
 *
 * Called AT LAUNCH, not at settlement. Costs are already paid at launch and
 * there is no cancel, so there is no refund hazard — and settling instead leaves
 * a window worth exactly one full slot count: with three slots you would fire
 * three convoys into the ×2.00 cell simultaneously and all three would collect
 * the undiminished premium before anything bit. Charging at launch also means
 * the planner's price drops as the player commits, which is the feedback that
 * teaches the mechanic exists.
 *
 * Capped at 1: six convoys into one cell should not kill it for a week. Maximum
 * suppression is "pays no premium" and maximum recovery is one window, so
 * over-feeding a market is a wasted premium rather than a trap.
 *
 * @returns {number} the market's new saturation level
 */
export function recordDelivery(regionId, skillId, units, now = Date.now()) {
    const faction = getSkillFaction(skillId);
    const store = satiationStore();
    if (!faction || !store || !(units > 0)) return 0;
    const level = clamp01(satiationLevel(regionId, faction, now) + units / REGION_APPETITE);
    store[satiationKey(regionId, faction)] = { level, at: now };
    return level;
}

/**
 * What a region pays for a faction's signature right now — list price, less
 * however much of its appetite is already spent.
 *
 * Saturation eats the PREMIUM ONLY, never the floor. A flat market still pays
 * ×1.00: it stops paying extra, it does not refuse the cargo. That keeps every
 * quoted price inside [DEMAND_MIN, DEMAND_MAX] — the band `operationYield` and
 * the UI's colour rules both assume — and it keeps a saturated region a worse
 * choice rather than an illegal one.
 */
export function factionDemandMultiplier(regionId, faction, now = Date.now()) {
    const base = baseFactionDemand(regionId, faction);
    const level = satiationLevel(regionId, faction, now);
    if (level <= 0) return base;
    return +(DEMAND_MIN + (base - DEMAND_MIN) * (1 - level)).toFixed(2);
}

/** The premium a region pays for cargo drawn from a skill signature. */
export function demandMultiplier(regionId, skillId, now = Date.now()) {
    return factionDemandMultiplier(regionId, getSkillFaction(skillId), now);
}

/**
 * A region's demand, best-paying first — the shape the UI reads.
 *
 * Sorted by what it pays NOW, not by its list price, so the order re-arranges as
 * markets are fed and recover. That re-ordering is the affordance: the rotation
 * the player is meant to discover is visible as the list moving.
 *
 * `multiplier` is the effective price for the same reason — it is the number a
 * convoy collects. `base` rides alongside it because a discount is only readable
 * next to what it was discounted from.
 *
 * @returns {Array<{faction:string, name:string, share:number, multiplier:number,
 *                  base:number, saturation:number, recoveryMs:number}>}
 */
export function regionDemandProfile(regionId, now = Date.now()) {
    const mix = regionFactionMix(regionId);
    return Object.keys(mix)
        .map(faction => ({
            faction,
            name: config.factionSkills?.[faction]?.name || faction,
            share: mix[faction],
            multiplier: factionDemandMultiplier(regionId, faction, now),
            base: baseFactionDemand(regionId, faction),
            saturation: satiationLevel(regionId, faction, now),
            recoveryMs: satiationRecoveryMs(regionId, faction, now),
        }))
        .sort((a, b) => b.multiplier - a.multiplier);
}

/** Does a delivery at this multiplier earn the Writ trickle? */
export function earnsDemandWrit(multiplier) {
    return (multiplier || 0) >= DEMAND_WRIT_THRESHOLD;
}
